import { WarningOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { message, Popconfirm } from 'antd';
import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import {
    type DeleteRoleMutation,
    type DeleteRoleMutationVariables,
    DeleteRoleDocument,
} from '../../../api/mutations/deleteRole';

export type DeleteRoleProps = {
    role: RoleSpecsFragment;
    onClose: () => void;
    visible: boolean;
};

const DeleteRole = ({ role, onClose, visible }: DeleteRoleProps) => {
    const { t } = useTranslation(['roleDetails']);
    const navigate = useNavigate();

    const apolloClient = useApolloClient();
    const roleId = role.id;

    const onDelete = useCallback(async () => {
        message.loading({
            content: t('roleDetails:deleteRole.messages.submitting'),
            key: 'primary',
            duration: 0,
        });

        await apolloClient
            .mutate<DeleteRoleMutation, DeleteRoleMutationVariables>({
                mutation: DeleteRoleDocument,
                variables: { roleId },
            })
            .finally(() => {
                message.destroy('primary');
            });

        navigate('/admin/accesses/roles');
    }, [t, apolloClient, roleId, navigate]);

    return (
        <Popconfirm
            cancelText={t('roleDetails:deleteRole.cancelText')}
            icon={<WarningOutlined style={{ color: 'red' }} />}
            okText={t('roleDetails:deleteRole.okText')}
            onCancel={onClose}
            onConfirm={onDelete}
            open={visible}
            placement="left"
            title={
                role.users.length > 0
                    ? t('roleDetails:deleteRole.hasUserAssignedMessage')
                    : t('roleDetails:deleteRole.message')
            }
        />
    );
};

export default DeleteRole;

export const useDeleteRoleConfirmation = (role: DeleteRoleProps['role']) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => <DeleteRole onClose={actions.close} role={role} visible={visible} />,
    };
};
